import { Icon } from "@design-system/Icon";
import { FlexRow } from "@design-system/Layout/Flex";
import { ColumnDef } from "@tanstack/react-table";
import { EditPencil, MoreHoriz } from "iconoir-react";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { User, UserRole } from "../../../../../generated/client/graphql";
import {
    ActionDropdownMenu,
    ActionDropdownMenuContent,
    ActionDropdownMenuItem,
    ActionDropdownMenuTrigger,
} from "../../../../components/ActionDropdown";
import ButtonRegular from "../../../../components/Button/ButtonRegular";
import { ButtonType } from "../../../../components/Button/ButtonShared";
import ButtonSmall from "../../../../components/Button/ButtonSmall";
import ActionWithConfirmation from "../../../../components/Modal/ActionWithConfirmation";
import Header from "../../../../components/Table/Header";
import Avatar from "../../../../components/User/Avatar";
import { useDeleteUser } from "../../data";
import { I18_DEPARTMENTS_NAMESPACE } from "../NewUserModal/services";
import { toUpdateUserIdAtom } from "../UpdateUserModal";
import "./i18n";

const HeaderTitle = ({ i18n_key }: { i18n_key: string }) => {
    const { t } = useTranslation("UsersTable");
    return <span>{t(i18n_key)}</span>;
};

const emailCol: ColumnDef<User> = {
    accessorKey: "email",
    header: ({ column }) => {
        const { t } = useTranslation("UsersTable");
        return <Header title={t("user")} column={column} />;
    },
    cell: ({ row }) => {
        return (
            <div className="flex space-x-2 items-center">
                <Avatar
                    initials={row.original.initials}
                    avatarUrl={row.original.avatar?.signedUrl}
                    size="large"
                />
                <div className="text-sm font-bold">
                    {row.original.firstName} {row.original.lastName}
                </div>
                <div className="text-xs font-regular">{row.original.email}</div>
            </div>
        );
    },
    meta: {
        colWidth: "30%",
    },
};

const roleCol: ColumnDef<User> = {
    accessorKey: "permission.role",
    header: ({ column }) => {
        const { t } = useTranslation("UsersTable");
        return <Header title={t("role")} column={column} />;
    },
    cell: ({ row }) => {
        const { t } = useTranslation("UsersTable");

        return (
            <div className="text-sm font-bold">
                {row.original.permission?.role &&
                    t(row.original.permission?.role)}
            </div>
        );
    },
};
const departmentCol: ColumnDef<User> = {
    accessorKey: "department",
    header: ({ column }) => {
        const { t } = useTranslation("UsersTable");
        return <Header title={t("department")} column={column} />;
    },
    cell: ({ row }) => {
        const { t } = useTranslation(I18_DEPARTMENTS_NAMESPACE);
        return <div>{t(row.original.department || "other")}</div>;
    },
};

const statusCol: ColumnDef<User> = {
    id: "status",
    header: () => <HeaderTitle i18n_key="status" />,

    cell: ({ row }) => {
        const { t } = useTranslation("UsersTable");
        if (row.original?.permission?.role === UserRole.Contributor) {
            return <></>;
        } else if (row.original.clerkUser?.emailVerified) {
            return <span className="text-green-500">{t("verified")}</span>;
        } else {
            return (
                <span className="text-secondary text-sm font-regular">
                    {t("pending")}
                </span>
            );
        }
    },
};
const actionCol: ColumnDef<User> = {
    id: "actions",
    cell: ({ row }) => {
        const { t } = useTranslation("UsersTable");
        const setToUpdateUserId = useSetAtom(toUpdateUserIdAtom);
        const { mutate, isPending } = useDeleteUser();

        const handleClick = (close: () => void) => () =>
            mutate(row.original.id, { onSuccess: close });
        return (
            <div className="flex justify-end">
                <ActionDropdownMenu modal={false}>
                    <ActionDropdownMenuTrigger asChild>
                        <ButtonSmall
                            variant={ButtonType.OUTLINED}
                            IconLeft={MoreHoriz}
                        />
                    </ActionDropdownMenuTrigger>
                    <ActionDropdownMenuContent>
                        <ActionDropdownMenuItem
                            onSelect={() => {
                                setToUpdateUserId(row.original.id);
                            }}
                            Icon={({ className }) => (
                                <EditPencil className={className} />
                            )}
                            text={t("edit_contributor")}
                        />

                        <ActionWithConfirmation
                            TriggerButton={
                                <FlexRow
                                    w="full"
                                    gap="2"
                                    px="2"
                                    py="1.5"
                                    alignItems="center"
                                    className="h-[32px] hover:cursor-pointer hover:bg-[#FEEFEC] text-error-500"
                                >
                                    <Icon name="trash" size="sm" />
                                    <div>{t("delete_user")}</div>
                                </FlexRow>
                            }
                            title={t("delete_title")}
                            confirmButton={(close) => (
                                <ButtonRegular
                                    variant={ButtonType.FLAT_DANGER}
                                    onClick={handleClick(close)}
                                    loading={isPending}
                                >
                                    {t("delete_confirm_button")}
                                </ButtonRegular>
                            )}
                        >
                            <div>
                                {t("delete_text_1", {
                                    supplier: row.original.email,
                                })}
                                <br />
                            </div>
                        </ActionWithConfirmation>
                    </ActionDropdownMenuContent>
                </ActionDropdownMenu>
            </div>
        );
    },
    meta: {
        colWidth: "60px",
    },
};
export const columns = (supplierMode?: boolean): ColumnDef<User>[] =>
    supplierMode
        ? [emailCol, actionCol]
        : [emailCol, roleCol, departmentCol, statusCol, actionCol];
